import {
	Code,
	Grid,
	Input,
	LoadingOverlay,
	Modal,
	Select,
	Skeleton,
	Space,
	TextInput,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { useDisclosure } from '@mantine/hooks';
import { IconBuildingStore } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import type { SelectorGroupItem } from '../../components/BranchSelector';
import { SmallModal } from '../../components/SmallModal';
import { Title } from '../../components/Title';
import { FormButtons } from '../../components/form/FormButtons';
import { useUrlParams } from '../../hooks/useUrlParams';
import { inputIconStyle } from '../../theme/styling/iconStyling';
import { query } from '../../util/api/query';
import type { PharmaciesListDto } from '../../util/types/PharmaciesListDto';
import { mutateBlob } from './hooks/mutateBlob';
import { useExportForm } from './hooks/useExportForm';

interface ExportModalProps {
	close: () => void;
	isOpened: boolean;
	apiPath: string;
	title?: string;
	disablePdf?: boolean;
}

export function ExportModal({
	isOpened,
	close,
	apiPath,
	title,
	disablePdf = false,
}: ExportModalProps) {
	const { organisation } = useParams();

	const pharmaciesQuery = query<PharmaciesListDto>(
		`${organisation}/branches/list-pharmacies`,
	);
	const [pharmacies, setPharmacies] = useState<SelectorGroupItem[]>([]);

	const { form, inputs } = useExportForm(disablePdf ? 'Excel' : '');
	const urlParams = useUrlParams(inputs);

	const exportMutation = mutateBlob(`${apiPath}?${urlParams}`);

	const [
		isSuccessModalOpen,
		{ open: openSuccessModal, close: closeSuccessModal },
	] = useDisclosure(false);

	const downloadSpreadsheet = (fileContent: Blob) => {
		if (inputs.exportType === 'PDF') {
			const pdfBlob = new Blob([fileContent], { type: 'application/pdf' });
			const pdfUrl = URL.createObjectURL(pdfBlob);
			window.open(pdfUrl, '_blank');
			// No immediate revoke — allow browser to load it
		} else {
			const link = document.createElement('a');
			link.href = URL.createObjectURL(fileContent);
			link.download = `${inputs.fileName}.xlsx`;
			link.click();
			URL.revokeObjectURL(link.href);
		}
	};

	useEffect(() => {
		if (pharmaciesQuery.data) {
			const value = pharmaciesQuery.data.value;

			const comboOptions: SelectorGroupItem[] = [
				{ group: 'Areas', items: value.areas },
				{ group: 'Individual', items: value.pharmacies },
			];

			setPharmacies(comboOptions);
		}
	}, [pharmaciesQuery.data]);

	const resetAndCloseSuccess = () => {
		form.reset();
		closeSuccessModal();
	};

	return (
		<>
			<Modal
				opened={isOpened}
				onClose={() => {
					resetAndCloseSuccess();
					close();
				}}
				title={
					<>
						<Title label={title ?? 'Export'} />
						<Code mx={0} px={0}>
							{disablePdf ? 'Export data to Excel' : 'Export data to PDF or Excel'}
						</Code>
					</>
				}
				w={100}
				withCloseButton={true}
				size={'md'}
				centered
			>
				<SmallModal
					isOpen={isSuccessModalOpen}
					close={resetAndCloseSuccess}
					title={'File exported! '}
					body={
						'Your file has been exported. Please allow a moment for the download to complete...'
					}
				/>
				<LoadingOverlay visible={exportMutation.isLoading} />
				<Space h={6} />
				<form
					onSubmit={form.onSubmit(async () =>
						exportMutation.mutateAsync().then((e) => {
							downloadSpreadsheet(e);
							exportMutation.reset();
							openSuccessModal();
						}),
					)}
				>
					<Grid>
						<Grid.Col span={6}>
							<DatePickerInput
								label={'Start Date'}
								placeholder={'Insert start date...'}
								key={form.key('startDate')}
								{...form.getInputProps('startDate')}
							/>
						</Grid.Col>
						<Grid.Col span={6}>
							<DatePickerInput
								label={'End Date'}
								placeholder={'Insert end date...'}
								key={form.key('endDate')}
								{...form.getInputProps('endDate')}
							/>
						</Grid.Col>
						<Grid.Col span={12}>
							{!pharmaciesQuery.isLoading ? (
								<Select
									label={'Branch'}
									placeholder="Select branch associated with target..."
									data={pharmacies}
									clearable
									description={'Leave empty to export all records'}
									searchable
									key={form.key('branch')}
									{...form.getInputProps('branch')}
									data-cy="branchId-input"
									leftSection={<IconBuildingStore {...inputIconStyle} />}
								/>
							) : (
								<Input.Wrapper label={'Branch'}>
									<Skeleton
										height={8}
										mt={6}
										width="100%"
										radius={'xl'}
										h={25}
									/>
								</Input.Wrapper>
							)}
						</Grid.Col>
						<Grid.Col span={6}>
							<TextInput
								label={'File Name'}
								placeholder={'Insert name of new group...'}
								key={form.key('fileName')}
								{...form.getInputProps('fileName')}
							/>
						</Grid.Col>
						<Grid.Col span={6}>
							<Select
								label={'Type of Export'}
								placeholder={'Select export type...'}
								data={disablePdf ? ['Excel'] : ['Excel', 'PDF']}
								key={form.key('exportType')}
								{...form.getInputProps('exportType')}
							/>
						</Grid.Col>
					</Grid>
					<FormButtons
						close={() => {
							resetAndCloseSuccess();
							close();
						}}
						reducePaddingTop={false}
					/>
				</form>
			</Modal>
		</>
	);
}
